import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Select, Tabs } from "antd";

import CollapseHeader from "../../../../../core/common/collapse-header/collapse-header";
import { all_routes } from "../../../../router/all_routes";
import {
  deleteReportTemplate,
  downloadExtensivePayrollReport,
  getAllComapniesList,
  getAllEmployeesListDataByCompanyId,
  getDepartments,
  listReportTemplates,
  saveReportTemplate,
} from "../../../../../services/attendanceService";
import { notify } from "../../../../../services/toast.config";
import { getErrorMessage } from "../../../../../core/data/redux/utils/errorUtils";
import { formStateToTemplateConfig } from "../helpers/payrollTemplate.helper";
import PayrollReportForm from "../components/PayrollReportForm";
import PayrollHistoryTab from "../components/PayrollHistoryTab";
import PayrollTemplatesTab from "../components/PayrollTemplatesTab";
import SaveTemplateModal from "../components/modals/SaveTemplateModal";

import type {
  ExtensivePayrollPayload,
  PayrollTemplate,
  RawFormState,
  TemplateConfig,
} from "../types";

type CompanyOption = {
  key: string;
  value: number;
  label: string;
  type: string;
};

const filterOption = (input: string, option?: { label: string; value: any }) =>
  (option?.label ?? "").toString().toLowerCase().includes(input.toLowerCase());

const EMPTY_TEMPLATE_CONFIG: TemplateConfig = {
  department_ids: [],
  employee_ids: [],
  late_cutoff_time: "09:45",
  week_off_days: [0],
  late_forgiveness_count: 2,
  exceptions: [],
};

const PayrollReportPage: React.FC = () => {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  );
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [departments, setDepartments] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);

  const [activeTab, setActiveTab] = useState("download");
  const [templates, setTemplates] = useState<PayrollTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [prefill, setPrefill] = useState<{
    config: TemplateConfig;
    key: number;
  } | null>(null);
  const [appliedTemplateId, setAppliedTemplateId] = useState<number | null>(
    null,
  );

  const [downloadLoading, setDownloadLoading] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [pendingConfig, setPendingConfig] = useState<TemplateConfig | null>(
    null,
  );

  const userDetails = useSelector(
    (s: any) => s?.user?.user?.userDetails,
  );
  const roleChar = (userDetails?.role_char || "").toString().toUpperCase();
  const ownDepartment =
    userDetails?.department != null ? Number(userDetails.department) : null;

  const isUnrestricted = useMemo(() => {
    if (roleChar === "ADMIN" || roleChar === "SUPER ADMIN") return true;
    if (roleChar === "EMPLOYEEADMIN" && Number(userDetails?.department_access) === 1)
      return true;
    return false;
  }, [roleChar, userDetails?.department_access]);

  const scopedDepartments = useMemo(
    () =>
      isUnrestricted || ownDepartment == null
        ? departments
        : departments.filter((d: any) => Number(d.value) === ownDepartment),
    [departments, isUnrestricted, ownDepartment],
  );

  const scopedEmployees = useMemo(
    () =>
      isUnrestricted || ownDepartment == null
        ? employees
        : employees.filter((e: any) => Number(e.department) === ownDepartment),
    [employees, isUnrestricted, ownDepartment],
  );

  useEffect(() => {
    fetchCompanies();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (!selectedCompany?.value) return;
    fetchEmployees();
    loadTemplates();
    setPrefill(null);
    setAppliedTemplateId(null);
  }, [selectedCompany?.value, selectedCompany?.type]);

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const res = await getAllComapniesList();
      const list = res?.data?.data?.data || [];
      const mapped: CompanyOption[] = list.map((c: any) => ({
        key: `${c.id}-${c.type}`,
        value: c.id,
        label: `${c.name} - ${c.type}`,
        type: c.type,
      }));
      setCompanies(mapped);
      if (mapped.length > 0) setSelectedCompany(mapped[0]);
    } catch (err) {
      notify.error(getErrorMessage(err));
    } finally {
      setCompaniesLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await getDepartments();
      const list = res?.data?.data?.data || [];
      setDepartments(
        list.map((d: any) => ({ value: d.key, label: d.Department })),
      );
    } catch (err) {
      setDepartments([]);
    }
  };

  const fetchEmployees = async () => {
    if (!selectedCompany) return;
    try {
      const res = await getAllEmployeesListDataByCompanyId({
        company_id: selectedCompany.value,
        company_type: selectedCompany.type,
      });
      setEmployees(res?.data?.data?.allUsers || []);
    } catch (err) {
      setEmployees([]);
    }
  };

  const loadTemplates = async () => {
    if (!selectedCompany?.value) return;
    setTemplatesLoading(true);
    try {
      const res = await listReportTemplates({
        c_id: selectedCompany.value,
        c_type: selectedCompany.type,
      });
      setTemplates(res?.data?.data || []);
    } catch (err) {
      notify.error(getErrorMessage(err));
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleCompanyChange = (key: string) => {
    const match = companies.find((c) => c.key === key);
    if (match) setSelectedCompany(match);
  };

  const handleApplyTemplate = (template: PayrollTemplate) => {
    setPrefill((p) => ({
      config: template.template_config,
      key: (p?.key ?? 0) + 1,
    }));
    setAppliedTemplateId(template.id);
    setActiveTab("download");
  };

  const handleDeleteTemplate = async (template: PayrollTemplate) => {
    try {
      await deleteReportTemplate({
        id: template.id,
        c_id: selectedCompany?.value,
        c_type: selectedCompany?.type,
      });
      notify.success("Template deleted");
      if (appliedTemplateId === template.id) setAppliedTemplateId(null);
      loadTemplates();
    } catch (err) {
      notify.error(getErrorMessage(err));
    }
  };

  const handleDownload = async (payload: ExtensivePayrollPayload) => {
    if (!selectedCompany) {
      notify.error("Please select a company first");
      return;
    }
    setDownloadLoading(true);
    try {
      const resp: any = await downloadExtensivePayrollReport(
        { ...payload, template_id: appliedTemplateId ?? null },
        selectedCompany as any,
      );
      if (!resp || !resp.data) throw new Error("Empty response from server");
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payroll-Report-${payload.month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      notify.success("Payroll report downloaded successfully");
      setHistoryRefreshKey((k) => k + 1);
    } catch (err: any) {
      notify.error(
        err?.response?.data?.message?.[0]?.msg ||
        err?.message ||
        "Failed to download payroll report",
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSaveTemplateClick = (state: RawFormState) => {
    setPendingConfig(formStateToTemplateConfig(state));
    setShowSaveModal(true);
  };

  const handleSaveTemplate = async (name: string, description: string) => {
    if (!pendingConfig || !selectedCompany) return;
    setSavingTemplate(true);
    try {
      await saveReportTemplate({
        c_id: selectedCompany.value,
        c_type: selectedCompany.type,
        template_name: name,
        description,
        ...pendingConfig,
      });
      notify.success("Template saved");
      setShowSaveModal(false);
      setPendingConfig(null);
      loadTemplates();
    } catch (err) {
      notify.error(getErrorMessage(err));
    } finally {
      setSavingTemplate(false);
    }
  };

  const templateOptions = templates.map((t) => ({
    value: t.id,
    label: t.template_name,
  }));

  const downloadTab = (
    <div>
      <div
        className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 p-3"
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, color: "#0f172a" }}>
            Configure Report
          </div>
          <div className="small" style={{ color: "#94a3b8" }}>
            Set the month, scope and exception days — or start from a saved
            template.
          </div>
        </div>
        <div style={{ minWidth: 280 }}>
          <Select
            allowClear
            showSearch
            filterOption={filterOption}
            style={{ width: "100%", minHeight: 38 }}
            placeholder="Start from a template"
            value={appliedTemplateId ?? undefined}
            options={templateOptions}
            onChange={(v) => {
              if (v == null) {
                setAppliedTemplateId(null);
                setPrefill((p) => ({
                  config: EMPTY_TEMPLATE_CONFIG,
                  key: (p?.key ?? 0) + 1,
                }));
                return;
              }
              const t = templates.find((x) => x.id === v);
              if (t) handleApplyTemplate(t);
            }}
          />
        </div>
      </div>
      <PayrollReportForm
        selectedCompany={selectedCompany}
        departments={scopedDepartments}
        employees={scopedEmployees}
        loading={downloadLoading}
        prefill={prefill}
        isUnrestricted={isUnrestricted}
        lockedDepartmentId={ownDepartment}
        onDownload={handleDownload}
        onSaveTemplateClick={handleSaveTemplateClick}
      />
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2
              className="mb-1"
              style={{ fontSize: 22, fontWeight: 600, color: "#0f172a" }}
            >
              Payroll Report
            </h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.attendanceadmin}>Attendance</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Payroll Report
                </li>
              </ol>
            </nav>
          </div>

          <div className="d-flex align-items-center ms-auto flex-wrap gap-2 mb-2">
            <button
              type="button"
              className="btn btn-outline-info d-inline-flex align-items-center"
              onClick={() => navigate(all_routes.attendanceadmin)}
              style={{ minHeight: 38 }}
            >
              <i className="ti ti-arrow-left me-1" />
              Back
            </button>
            <Select
              showSearch
              filterOption={filterOption}
              style={{ width: 260, minHeight: 38 }}
              placeholder="Select Company"
              value={selectedCompany?.key}
              loading={companiesLoading}
              onChange={handleCompanyChange}
              options={companies.map((c) => ({
                value: c.key,
                label: c.label,
              }))}
            />
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="head-icons mb-0">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body bg-white rounded-3">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                { key: "download", label: "Download", children: downloadTab },
                {
                  key: "history",
                  label: "History",
                  children: (
                    <PayrollHistoryTab
                      selectedCompany={selectedCompany}
                      employees={scopedEmployees}
                      departments={scopedDepartments}
                      refreshKey={historyRefreshKey}
                      active={activeTab === "history"}
                    />
                  ),
                },
                {
                  key: "templates",
                  label: "Templates",
                  children: (
                    <PayrollTemplatesTab
                      templates={templates}
                      loading={templatesLoading}
                      employees={scopedEmployees}
                      onApply={handleApplyTemplate}
                      onDelete={handleDeleteTemplate}
                    />
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2025 © CMS.</p>
        <p>
          Designed & Developed By <span className="text-primary">ZYVO</span>
        </p>
      </div>

      <SaveTemplateModal
        show={showSaveModal}
        loading={savingTemplate}
        onHide={() => setShowSaveModal(false)}
        onSave={handleSaveTemplate}
      />
    </div>
  );
};

export default PayrollReportPage;
